import { useState } from "react";
import { Search, Building2, AlertCircle, CheckCircle2, Clock, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusColors, statusDisplayMap } from "@/constants/projectMeta";
import { getProjectTrackStatus, type TrackStatus } from "@/utils/projectTrackStatus";
import { format, parseISO } from "date-fns";

interface Project {
  id: string;
  name: string;
  client_id: string;
  status: string;
  start_date?: string;
  promised_delivery_date?: string;
  delivered_date?: string;
  confirm_date?: string;
  sold_for?: number | string | null;
  extra_value?: number | string | null;
  payment_schedule?: any;
  extra_value_payment_schedule?: any;
  clients?: { name: string };
}

interface ProjectsListProps {
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project) => void;
  onNewProject: () => void;
  isLoading?: boolean;
}

const TrackStatusBadge = ({ status }: { status: TrackStatus }) => {
  if (!status) return null;
  
  return (
    <Badge 
      variant="outline" 
      className={status === 'on_track' 
        ? 'text-green-700 border-green-200 bg-green-50' 
        : 'text-red-700 border-red-200 bg-red-50'
      }
    >
      {status === 'on_track' ? (
        <>
          <CheckCircle2 className="h-3 w-3 mr-1" />
          On Track
        </>
      ) : (
        <>
          <AlertCircle className="h-3 w-3 mr-1" />
          Off Track
        </>
      )}
    </Badge>
  );
};

export function ProjectsList({ projects, selectedProject, onSelectProject, onNewProject, isLoading }: ProjectsListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");

  // Helper function to check if project is relevant for current year (2025)
  const isProjectRelevantForCurrentYear = (project: Project) => {
    const currentYear = 2025;
    const startYear = project.start_date ? new Date(project.start_date).getFullYear() : null;
    const deliveredYear = project.delivered_date ? new Date(project.delivered_date).getFullYear() : null;
    const promisedDeliveryYear = project.promised_delivery_date ? new Date(project.promised_delivery_date).getFullYear() : null;
    
    // Projects that started in current year
    if (startYear === currentYear) return true;
    
    // Projects that started in previous year but delivered/promised to be delivered in current year
    if (startYear === currentYear - 1 && (deliveredYear === currentYear || promisedDeliveryYear === currentYear)) return true;
    
    // Projects that started in previous year and are still in progress (assuming they continue to current year)
    if (startYear === currentYear - 1 && (project.status === 'in_progress' || project.status === 'confirmed')) return true;
    
    return false;
  };

  // Helper function to get the start date for display and sorting
  const getProjectDate = (project: Project) => {
    return project.start_date;
  };

  console.log("DEBUG ProjectsList: Raw projects data:", projects?.length, projects?.slice(0, 3));
  console.log("DEBUG ProjectsList: Filters:", { searchTerm, statusFilter, yearFilter });
  
  const filteredProjects = projects?.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.clients?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;
    
    // Year filtering - smart filtering for current year projects
    let matchesYear = true;
    if (yearFilter !== "all") {
      if (yearFilter === "2025") {
        matchesYear = isProjectRelevantForCurrentYear(project);
      } else {
        // Regular year filtering for other years
        const projectDate = getProjectDate(project);
        if (projectDate) {
          const projectYear = new Date(projectDate).getFullYear().toString();
          matchesYear = projectYear === yearFilter;
        } else {
          matchesYear = yearFilter === "all";
        }
      }
    }
    
    const passes = matchesSearch && matchesStatus && matchesYear;
    if (!passes) {
      console.log("DEBUG ProjectsList: Project filtered out:", {
        name: project.name,
        client: project.clients?.name,
        matchesSearch,
        matchesStatus,
        matchesYear,
        yearFilter,
        projectDate: getProjectDate(project)
      });
    }
    
    return passes;
  }).sort((a, b) => {
    // Sort by most recent date first (newest to oldest)
    const dateA = getProjectDate(a);
    const dateB = getProjectDate(b);
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  }) || [];

  if (isLoading) {
    return (
      <div className="w-96 border-r border-border h-full p-4 space-y-4">
        <div className="space-y-4">
          <div className="h-10 bg-muted animate-pulse rounded" />
          <div className="h-10 bg-muted animate-pulse rounded" />
        </div>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="w-96 border-r border-border h-full flex flex-col">
      <div className="p-4 border-b border-border space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Projects</h1>
          <Button onClick={onNewProject}>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="delivered">Ended</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              <SelectItem value="2023">2023</SelectItem>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredProjects.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No projects found</p>
          </div>
        ) : (
          filteredProjects.map((project) => {
            const trackStatus = getProjectTrackStatus(project);
            const isSelected = selectedProject?.id === project.id;
            
            return (
              <Card
                key={project.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'ring-2 ring-primary bg-primary/5' : ''
                }`}
                onClick={() => onSelectProject(project)}
              >
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <h3 className="font-medium line-clamp-1 flex-1">{project.name}</h3>
                      <Badge 
                        className={`ml-2 ${
                          project.status === 'in_progress' && trackStatus === 'off_track' 
                            ? 'bg-red-100 text-red-800 border-red-200' 
                            : statusColors[project.status as keyof typeof statusColors]
                        }`}
                        variant="secondary"
                      >
                        {statusDisplayMap[project.status as keyof typeof statusDisplayMap]}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4" />
                        <span className="line-clamp-1">{project.clients?.name}</span>
                      </div>
                      {(() => {
                        const displayDate = getProjectDate(project);
                        if (displayDate) {
                          return (
                            <div className="flex items-center">
                              <span className="text-xs font-semibold text-foreground/70">
                                {format(parseISO(displayDate), "MMM dd")}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}